import React, { useCallback, useState, useMemo, useEffect, Fragment } from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import classnames from 'classnames';
import Promise from 'bluebird';
import {
  Dialog, DialogFooter,
  PrimaryButton, DefaultButton,
  TextField,
  Stack,
} from '@bingads-webui-react/fabric-control';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { HelpPopupInline } from '@bingads-webui-react/help-popup';
import { UiBlocker } from '@bingads-webui-react/ui-blocker';
import { getDialogImageExtensionStyle } from '../styles/image-extension-dialog-style';
import {
  fieldsConfig,
  validateAllFields,
  validateField,
  FieldType,
  FieldLimit,
  DefaultImageAltText,
} from './image-extension-fields-config';

export const CreateImageExtensionDialog = ({
  i18n,
  createImageExtension,
  selectedImage,
  onCreateSuccess,
  onCancel,
  adCreatorScenario,
  permissions,
}) => {
  const isImageAltTextPrefillEnabled = _.result(permissions, ['dynamic', 'ImageAltTextPrefill'], false);

  const dialogContentProps = { title: i18n.getString(_TL_('New image extension')) };
  const classes = useDefaultStyles(getDialogImageExtensionStyle);
  const [loading, setLoading] = useState(false);

  const [fieldValues, setFieldValues] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});

  const [disableCreate, setDisableCreate] = useState(false);

  const logActivity = useMemo(() => {
    if (adCreatorScenario && adCreatorScenario.createActivity) {
      return adCreatorScenario.createActivity('New image to extension');
    }
    return null;
  }, [adCreatorScenario]);

  const logTrace = useCallback((...args) => {
    if (logActivity) {
      logActivity.trace(...args);
    }
  }, [logActivity]);

  const onCreateButtonClicked = useCallback(() => {
    const errors = validateAllFields(fieldValues, setFieldErrors);

    if (_.isEmpty(errors)) {
      setLoading(true);
      // After a design change, createImageExtension is sync. Keep promise to support both modes
      Promise.resolve(createImageExtension(fieldValues, selectedImage))
        .finally(() => {
          setLoading(false);
        })
        .then(() => {
          logTrace('New image extension dialog - Save button clicked');
          onCreateSuccess();
        });
    } else {
      logTrace(`New image extension dialog - failed to save, errors: ${JSON.stringify(errors)}`);
      setDisableCreate(true);
    }
  });

  const onFieldChangeFactory = (type, limit) => (e, newValue) => {
    if (_.size(newValue) <= limit) {
      setFieldValues({
        ...fieldValues,
        [type]: newValue,
      });
    }
    const errors = validateField(type, newValue, setFieldErrors);
    if (_.isEmpty(errors)) {
      setDisableCreate(false);
    }
  };

  const onCancelClick = useCallback(() => {
    logTrace('New image extension dialog - Cancel button clicked');
    onCancel();
  }, [logTrace, onCancel]);

  const onDismiss = useCallback(() => {
    logTrace('New image extension dialog - Dialog dismissed');
    onCancel();
  }, [logTrace, onCancel]);

  useEffect(() => {
    logTrace('New image extension dialog - Dialog is shown');
  }, [logTrace]);

  useEffect(() => {
    if (isImageAltTextPrefillEnabled) {
      let prefillAltText = DefaultImageAltText;
      const imageName = _.result(selectedImage, ['0', 'text'], null);
      if (imageName && _.isString(imageName) && _.size(imageName.trim()) > 0 && _.size(imageName.trim()) <= FieldLimit.AltText) {
        prefillAltText = imageName.trim();
      }

      setFieldValues({
        ...fieldValues,
        [FieldType.AltText]: prefillAltText,
      });
      validateField(FieldType.AltText, prefillAltText, setFieldErrors);
    }
  // Only prefill image alt text in the beginning
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Dialog
      hidden={false}
      onDismiss={onDismiss}
      dialogContentProps={dialogContentProps}
      modalProps={{
        isBlocking: true,
        topOffsetFixed: true,
        className: classnames(classes.container, 'create-img-ex-dialog'),
      }}
      minWidth={420}
      isClickableOutsideFocusTrap
    >
      <UiBlocker
        blocking={loading}
        loadingText={i18n.getString(_TL_('Creating ...'))}
      >
        <Stack tokens={{ childrenGap: 24 }}>
          {
            fieldsConfig.map((field) => {
              const {
                type,
                limit,
                labelKeyForLocalization,
                description,
                helpTopic,
                required,
              } = field;
              const placeholder = i18n.getString(_TL_('{{limit}} characters max'), { limit });

              return (
                <TextField
                  className={`property-${type}`}
                  key={type}
                  label={i18n.getString(labelKeyForLocalization)}
                  onRenderLabel={(props, originalRenderer) => (
                    <Fragment>
                      <div className={classes.labelContainer}>
                        {originalRenderer(props)}
                        <HelpPopupInline topic={helpTopic} ariaLabel={i18n.getString(_TL_('Learn more'))} />
                      </div>
                      {description &&
                        <div className={classes.description}>
                          {i18n.getString(description)}
                        </div>
                      }
                    </Fragment>
                  )}
                  placeholder={placeholder}
                  onChange={onFieldChangeFactory(type, limit)}
                  errorMessage={fieldErrors[type] ? i18n.getString(fieldErrors[type]) : ''}
                  value={fieldValues[type] || ''}
                  required={required}
                />
              );
          })
        }
        </Stack>

        <DialogFooter>
          <PrimaryButton
            onClick={onCreateButtonClicked}
            text={i18n.getString(_TL_('Save'))}
            disabled={disableCreate}
          />
          <DefaultButton onClick={onCancelClick} text={i18n.getString(_TL_('Cancel'))} />
        </DialogFooter>
      </UiBlocker>
    </Dialog>
  );
};

CreateImageExtensionDialog.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  createImageExtension: PropTypes.func.isRequired,
  selectedImage: PropTypes.arrayOf(PropTypes.any).isRequired,
  onCreateSuccess: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  adCreatorScenario: PropTypes.shape({}),
  permissions: PropTypes.shape({
    dynamic: PropTypes.shape({
    }),
  }),
};

CreateImageExtensionDialog.defaultProps = {
  adCreatorScenario: undefined,
  permissions: {},
};
